import { ref, reactive } from 'vue'
import { VALIDATION } from '@/utils/constants'
import { sanitizeInput, validatePasswordStrength } from '@/utils/security'

export function useValidation() {
  const errors = reactive({})
  const isValid = ref(true)

  // Clear specific error
  const clearError = (field) => {
    if (errors[field]) {
      delete errors[field]
    }
    updateValidationState()
  }

  // Set error for field
  const setError = (field, message) => {
    errors[field] = message
    updateValidationState()
  }

  // Clear all errors
  const clearAllErrors = () => {
    Object.keys(errors).forEach((key) => delete errors[key])
    updateValidationState()
  }

  // Update validation state
  const updateValidationState = () => {
    isValid.value = Object.keys(errors).length === 0
  }

  // Validate email
  const validateEmail = (email, fieldName = 'email') => {
    if (!email) {
      setError(fieldName, 'Email is required')
      return false
    }
    if (!VALIDATION.EMAIL_REGEX.test(email)) {
      setError(fieldName, 'Please enter a valid email address')
      return false
    }
    clearError(fieldName)
    return true
  }

  // Validate password
  const validatePassword = (password, fieldName = 'password') => {
    if (!password) {
      setError(fieldName, 'Password is required')
      return false
    }
    if (password.length < VALIDATION.PASSWORD_MIN_LENGTH) {
      setError(
        fieldName,
        `Password must be at least ${VALIDATION.PASSWORD_MIN_LENGTH} characters long`
      )
      return false
    }
    if (!VALIDATION.PASSWORD_REGEX.LOWERCASE.test(password)) {
      setError(fieldName, 'Password must contain at least one lowercase letter')
      return false
    }
    if (!VALIDATION.PASSWORD_REGEX.UPPERCASE.test(password)) {
      setError(fieldName, 'Password must contain at least one uppercase letter')
      return false
    }
    if (!VALIDATION.PASSWORD_REGEX.NUMBER.test(password)) {
      setError(fieldName, 'Password must contain at least one number')
      return false
    }
    if (!VALIDATION.PASSWORD_REGEX.SPECIAL_CHAR.test(password)) {
      setError(fieldName, 'Password must contain at least one special character (@$!%*?&)')
      return false
    }
    clearError(fieldName)
    return true
  }

  // Validate name
  const validateName = (name, fieldName) => {
    if (!name || !name.trim()) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`)
      return false
    }
    if (name.trim().length < VALIDATION.NAME_MIN_LENGTH) {
      setError(
        fieldName,
        `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${
          VALIDATION.NAME_MIN_LENGTH
        } characters`
      )
      return false
    }
    if (!/^[a-zA-Z\s]+$/.test(name.trim())) {
      setError(
        fieldName,
        `${
          fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
        } can only contain letters and spaces`
      )
      return false
    }
    clearError(fieldName)
    return true
  }

  // Validate password confirmation
  const validatePasswordConfirmation = (
    password,
    confirmPassword,
    fieldName = 'confirmPassword'
  ) => {
    if (!confirmPassword) {
      setError(fieldName, 'Please confirm your password')
      return false
    }
    if (password !== confirmPassword) {
      setError(fieldName, 'Passwords do not match')
      return false
    }
    clearError(fieldName)
    return true
  }

  // Validate required field
  const validateRequired = (value, fieldName) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`)
      return false
    }
    clearError(fieldName)
    return true
  }

  // Sanitize and validate text input
  const validateAndSanitizeText = (text, fieldName, options = {}) => {
    const {
      required = true,
      minLength = 1,
      maxLength = 500,
      allowHtml = false,
    } = options

    // Required validation
    if (required && (!text || !text.trim())) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`)
      return { isValid: false, sanitizedValue: '' }
    }

    if (!text) {
      return { isValid: true, sanitizedValue: '' }
    }

    // Sanitize input to prevent XSS
    const sanitized = sanitizeInput(text, { 
      allowHtml, 
      maxLength,
      removeScripts: true,
      removeEvents: true 
    })

    // Length validation
    if (sanitized.length < minLength) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${minLength} characters`)
      return { isValid: false, sanitizedValue: sanitized }
    }

    if (sanitized.length > maxLength) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be less than ${maxLength} characters`)
      return { isValid: false, sanitizedValue: sanitized }
    }

    clearError(fieldName)
    return { isValid: true, sanitizedValue: sanitized }
  }

  // Enhanced password validation with strength checking
  const validatePasswordStrong = (password, fieldName = 'password') => {
    const basicValidation = validatePassword(password, fieldName)
    if (!basicValidation) {
      return { isValid: false, strength: null }
    }

    const strengthResult = validatePasswordStrength(password)
    
    // Require at least "Fair" strength
    if (strengthResult.score < 3) {
      setError(fieldName, `Password strength: ${strengthResult.strength}. ${strengthResult.feedback.join(', ')}`)
      return { isValid: false, strength: strengthResult }
    }

    clearError(fieldName)
    return { isValid: true, strength: strengthResult }
  }

  // Validate URL input
  const validateUrl = (url, fieldName, options = {}) => {
    const { required = false, allowedDomains = [] } = options

    if (!url && !required) {
      clearError(fieldName)
      return true
    }

    if (!url && required) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`)
      return false
    }

    // Basic URL format validation
    try {
      new URL(url)
    } catch (e) {
      // Check for relative URLs
      if (!url.startsWith('/') && !url.startsWith('#')) {
        setError(fieldName, 'Please enter a valid URL')
        return false
      }
    }

    // Check for dangerous protocols
    const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:']
    if (dangerousProtocols.some(protocol => url.toLowerCase().startsWith(protocol))) {
      setError(fieldName, 'Invalid URL protocol')
      return false
    }

    clearError(fieldName)
    return true
  }

  // Validate phone number
  const validatePhone = (phone, fieldName = 'phone') => {
    if (!phone) {
      setError(fieldName, 'Phone number is required')
      return false
    }

    // Remove all non-digits for validation
    const digitsOnly = phone.replace(/\D/g, '')
    
    // Check for valid length (10-15 digits)
    if (digitsOnly.length < 10 || digitsOnly.length > 15) {
      setError(fieldName, 'Please enter a valid phone number')
      return false
    }

    clearError(fieldName)
    return true
  }

  // Validate date input
  const validateDate = (date, fieldName, options = {}) => {
    const { required = false, minAge = null, maxAge = null } = options

    if (!date && !required) {
      clearError(fieldName)
      return true
    }

    if (!date && required) {
      setError(fieldName, `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`)
      return false
    }

    const dateObj = new Date(date)
    if (isNaN(dateObj.getTime())) {
      setError(fieldName, 'Please enter a valid date')
      return false
    }

    const today = new Date()
    const age = today.getFullYear() - dateObj.getFullYear()

    if (minAge && age < minAge) {
      setError(fieldName, `Age must be at least ${minAge} years`)
      return false
    }

    if (maxAge && age > maxAge) {
      setError(fieldName, `Age must be less than ${maxAge} years`)
      return false
    }

    // Check for reasonable date range (not too far in past/future)
    const minDate = new Date('1900-01-01')
    const maxDate = new Date()
    maxDate.setFullYear(maxDate.getFullYear() + 10)

    if (dateObj < minDate || dateObj > maxDate) {
      setError(fieldName, 'Please enter a valid date')
      return false
    }

    clearError(fieldName)
    return true
  }

  // Sanitize form data object
  const sanitizeFormData = (data) => {
    const sanitized = {}
    for (const [key, value] of Object.entries(data)) {
      if (typeof value === 'string') {
        sanitized[key] = sanitizeInput(value, {
          allowHtml: false,
          removeScripts: true,
          removeEvents: true
        })
      } else {
        sanitized[key] = value
      }
    }
    return sanitized
  }

  return {
    errors,
    isValid,
    clearError,
    setError,
    clearAllErrors,
    validateEmail,
    validatePassword,
    validateName,
    validatePasswordConfirmation,
    validateRequired,
    validateAndSanitizeText,
    validatePasswordStrong,
    validateUrl,
    validatePhone,
    validateDate,
    sanitizeFormData,
  }
}
